import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Bus, Plus, Wrench, ShieldCheck, Building2 } from "lucide-react";
import { toast } from "sonner";
import RowActions from "../../components/RowActions";
import { useSchools, SchoolSelect } from "../../components/SchoolSelect";
import { localName, localFullName } from "../../lib/localName";

const EMPTY = { school_id: "", plate_number: "", model: "", capacity: 30, year: "", color: "", insurance_company: "", insurance_expiry: "", license_expiry: "", status: "active", notes: "" };

export default function VehiclesPage() {
  const {  t , lang } = useI18n();
  const { schools, schoolId, setSchoolId, schoolName } = useSchools();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const [maintOpen, setMaintOpen] = useState(null); // vehicle id
  const [maintList, setMaintList] = useState([]);
  const [maintForm, setMaintForm] = useState({ date: "", type: "oil_change", cost: 0, description: "", next_due: "" });

  const load = () => { if (schoolId) api.get(`/vehicles?school_id=${schoolId}`).then(r => setItems(r.data || [])); };
  useEffect(() => { load(); }, [schoolId]);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, school_id: schoolId }); setOpen(true); };
  const openEdit = (v) => { setEditing(v); setForm({ ...EMPTY, ...v, year: v.year || "" }); setOpen(true); };

  const save = async () => {
    if (!form.school_id) { toast.error(t("select_school")); return; }
    try {
      const payload = { ...form, capacity: Number(form.capacity) || 0, year: form.year ? Number(form.year) : null };
      if (editing) await api.put(`/vehicles/${editing.id}`, payload);
      else await api.post("/vehicles", payload);
      toast.success(t("success")); setOpen(false);
      if (payload.school_id !== schoolId) setSchoolId(payload.school_id);
      else load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async (v) => {
    try { await api.delete(`/vehicles/${v.id}`); toast.success(t("success")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const openMaintenance = async (v) => {
    setMaintOpen(v);
    setMaintList([]);
    const r = await api.get(`/vehicles/${v.id}/maintenance`);
    setMaintList(r.data || []);
  };
  const addMaintenance = async () => {
    try {
      await api.post(`/vehicles/${maintOpen.id}/maintenance`, { ...maintForm, vehicle_id: maintOpen.id, cost: Number(maintForm.cost) || 0 });
      toast.success(t("success"));
      const r = await api.get(`/vehicles/${maintOpen.id}/maintenance`);
      setMaintList(r.data || []);
      setMaintForm({ date: "", type: "oil_change", cost: 0, description: "", next_due: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-3"><Bus className="w-7 h-7 text-primary" /> {t("vehicles")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("transport")} · {t("maintenance")} · {t("insurance_company")}</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <SchoolSelect schools={schools} value={schoolId} onChange={setSchoolId} testId="vehicles-school-select" />
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={openNew} data-testid="new-vehicle-btn"><Plus className="w-4 h-4 me-2" />{t("new_vehicle")}</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editing ? t("edit") : t("new_vehicle")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>{t("school")} *</Label>
                  <Select value={form.school_id || ""} onValueChange={v => setForm({ ...form, school_id: v })}>
                    <SelectTrigger data-testid="vehicle-school"><SelectValue placeholder={t("select_school")} /></SelectTrigger>
                    <SelectContent>{schools.map(s => <SelectItem key={s.id} value={s.id}>{localName(s, lang)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>{t("plate_number")}</Label><Input value={form.plate_number} onChange={e => setForm({ ...form, plate_number: e.target.value })} data-testid="vehicle-plate" /></div>
                <div><Label>{t("vehicle_model")}</Label><Input value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} /></div>
                <div><Label>{t("capacity_seats")}</Label><Input type="number" value={form.capacity} onChange={e => setForm({ ...form, capacity: e.target.value })} /></div>
                <div><Label>{t("year_label")}</Label><Input type="number" value={form.year} onChange={e => setForm({ ...form, year: e.target.value })} /></div>
                <div><Label>{t("insurance_company")}</Label><Input value={form.insurance_company} onChange={e => setForm({ ...form, insurance_company: e.target.value })} /></div>
                <div><Label>{t("insurance_expiry")}</Label><Input type="date" value={form.insurance_expiry} onChange={e => setForm({ ...form, insurance_expiry: e.target.value })} /></div>
                <div><Label>{t("license_expiry")}</Label><Input type="date" value={form.license_expiry} onChange={e => setForm({ ...form, license_expiry: e.target.value })} /></div>
                <div><Label>{t("status")}</Label>
                  <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">{t("active")}</SelectItem>
                      <SelectItem value="maintenance">{t("maintenance")}</SelectItem>
                      <SelectItem value="retired">{t("inactive")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={save} className="w-full" data-testid="vehicle-save">{t("save")}</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(v => {
          const expiringInsurance = v.insurance_expiry && new Date(v.insurance_expiry) < new Date(Date.now() + 30 * 86400000);
          return (
            <Card key={v.id} className="p-5 border shadow-none card-hover smooth" data-testid={`vehicle-${v.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary grid place-items-center"><Bus className="w-6 h-6" /></div>
                  <div>
                    <div className="font-semibold">{v.plate_number}</div>
                    <div className="text-xs text-muted-foreground">{v.model} · {v.capacity} {t("capacity_seats").split("(")[0]}</div>
                  </div>
                </div>
                <RowActions item={v} onEdit={openEdit} onDelete={remove} testIdPrefix="vehicle" />
              </div>
              <div className="mt-3 space-y-1 text-xs">
                <div className="flex items-center gap-2 text-muted-foreground" data-testid={`vehicle-school-${v.id}`}>
                  <Building2 className="w-3.5 h-3.5" /> {schoolName(v.school_id) || "—"}
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <ShieldCheck className="w-3.5 h-3.5" /> {v.insurance_company || "—"}
                  <span className={expiringInsurance ? "text-destructive font-medium" : ""}>· {v.insurance_expiry || "—"}</span>
                </div>
                <div className="text-muted-foreground">{t("status")}: <span className="font-medium text-foreground">{t(v.status === "active" ? "active" : v.status === "maintenance" ? "maintenance" : "inactive")}</span></div>
              </div>
              <Button variant="outline" size="sm" className="w-full mt-3" onClick={() => openMaintenance(v)} data-testid={`vehicle-maint-${v.id}`}>
                <Wrench className="w-3.5 h-3.5 me-2" /> {t("maintenance")}
              </Button>
            </Card>
          );
        })}
        {items.length === 0 && <Card className="col-span-full p-12 text-center text-muted-foreground border-dashed">{t("no_data")}</Card>}
      </div>

      <Dialog open={!!maintOpen} onOpenChange={(o) => !o && setMaintOpen(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle><Wrench className="w-4 h-4 me-2 inline" /> {t("maintenance")} — {maintOpen?.plate_number}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <Input type="date" value={maintForm.date} onChange={e => setMaintForm({ ...maintForm, date: e.target.value })} placeholder="Date" />
              <Select value={maintForm.type} onValueChange={v => setMaintForm({ ...maintForm, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="oil_change">Oil change</SelectItem>
                  <SelectItem value="repair">Repair</SelectItem>
                  <SelectItem value="inspection">Inspection</SelectItem>
                  <SelectItem value="tire">Tire</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <Input type="number" value={maintForm.cost} onChange={e => setMaintForm({ ...maintForm, cost: e.target.value })} placeholder={t("cost")} />
              <Input value={maintForm.description} onChange={e => setMaintForm({ ...maintForm, description: e.target.value })} placeholder={t("description")} />
            </div>
            <Button onClick={addMaintenance} className="w-full" size="sm" data-testid="maint-add">{t("save")}</Button>
            <div className="border-t border-border pt-3 max-h-64 overflow-y-auto">
              {maintList.map(m => (
                <div key={m.id} className="text-sm py-2 border-b border-border/50 flex justify-between gap-2">
                  <div><span className="font-medium">{m.type}</span> · <span className="text-muted-foreground">{m.date}</span></div>
                  <div className="text-primary font-mono">{m.cost} SAR</div>
                </div>
              ))}
              {maintList.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">{t("no_data")}</div>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
